//CategoryBottomSheet.jsx

import React, { useMemo, useState } from "react";

import { SplitRowQuarter } from "./ButtonGroup";
import { BottomWithButton } from "./BottomSheetGroup";
import PlaceholderB from "./PlaceholderB";
import down from "../icons/Arrow/down_tri.svg";

const CategoryBottomSheet = ({
    mainCategory,
    setMainCategory,
    mainCategories,
    subCategory,
    setSubCategory,
    subCategories,
    detailCategory,
    setDetailCategory,
    detailCategories,
    setCategory,
}) => {
    // 어떤 시트가 열렸는지: 'main' | 'sub' | 'detail' | null
    const [activeSheet, setActiveSheet] = useState(null);

    // 시트 안에서만 사용하는 임시 선택값 (저장 전까지 부모에 반영 X)
    const [draftMain, setDraftMain] = useState("0");
    const [draftSub, setDraftSub] = useState("0");
    const [draftDetail, setDraftDetail] = useState("0");

    const reference = 3;

    // ===== 부모에 이미 저장된 값으로 라벨 구하기 =====
    const mainLabel = useMemo(() => {
        if (!mainCategory || mainCategory === "0") return "";
        const found = mainCategories.find(
            (c) => c.biz_main_category_id === mainCategory
        );
        return found ? found.biz_main_category_name : "";
    }, [mainCategory, mainCategories]);

    const subLabel = useMemo(() => {
        if (!subCategory || subCategory === "0") return "";
        const found = subCategories.find(
            (c) => c.biz_sub_category_id === subCategory
        );
        return found ? found.biz_sub_category_name : "";
    }, [subCategory, subCategories]);

    const detailLabel = useMemo(() => {
        if (!detailCategory || detailCategory === "0") return "";
        const found = detailCategories.find(
            (c) => c.biz_detail_category_id === detailCategory
        );
        return found ? found.biz_detail_category_name : "";
    }, [detailCategory, detailCategories]);


    const hasMain = !!mainLabel;
    const hasSub = !!subLabel;

    // ===== 옵션 리스트 (CategorySelect 로직 그대로, '전체'는 제거) =====
    const mainOptions = mainCategories.map((cat) => ({
        value: cat.biz_main_category_id,
        label: `${cat.biz_main_category_name} (${cat.biz_sub_category_count})`,
    }));

    const filteredSubCategories =
        reference === 3
            ? subCategories.filter(
                (cat) =>
                    typeof cat.biz_sub_category_id === "string" &&
                    cat.biz_sub_category_id.startsWith(mainCategory)
            )
            : subCategories;

    const subOptions = filteredSubCategories.map((cat) => ({
        value: cat.biz_sub_category_id,
        label: `${cat.biz_sub_category_name} (${cat.biz_detail_cateogry_count})`,
    }));

    const filteredDetailCategories =
        reference === 3
            ? detailCategories.filter(
                (cat) =>
                    typeof cat.biz_detail_category_id === "string" &&
                    cat.biz_detail_category_id.startsWith(subCategory)
            )
            : detailCategories;

    const detailOptions = filteredDetailCategories.map((cat) => ({
        value: cat.biz_detail_category_id,
        label: cat.biz_detail_category_name,
    }));

    // ===== 시트 열 때 부모 값으로 draft 초기화 =====
    const openMainSheet = () => {
        setDraftMain(
            mainCategory && mainCategory !== "0" ? mainCategory : null
        );
        setActiveSheet("main");
    };

    const openSubSheet = () => {
        if (!hasMain) return;

        setDraftSub(
            subCategory && subCategory !== "0" ? subCategory : null
        );
        setActiveSheet("sub");
    };

    const openDetailSheet = () => {
        if (!hasMain) return;
        if (!hasSub) return;

        setDraftDetail(
            detailCategory && detailCategory !== "0" ? detailCategory : null
        );
        setActiveSheet("detail");
    };
    // ===== 닫기 / 저장 처리 =====
    const closeSheet = () => {
        // 값 반영 없이 그냥 닫기
        setActiveSheet(null);
    };

    const handleConfirmMain = () => {
        if (!draftMain || draftMain === "0") {
            setActiveSheet(null);
            return;
        }
        setMainCategory(draftMain);
        // 대분류 바뀌면 하위 초기화
        setSubCategory("0");
        setDetailCategory("0");
        setActiveSheet(null);
    };

    const handleConfirmSub = () => {
        if (!draftSub || draftSub === "0") {
            setActiveSheet(null);
            return;
        }
        setSubCategory(draftSub);
        setDetailCategory("0");
        setActiveSheet(null);
    };

    const handleConfirmDetail = () => {
        if (!draftDetail || draftDetail === "0") {
            setActiveSheet(null);
            return;
        }
        setDetailCategory(draftDetail);

        // 소분류까지 확정된 시점에 category 문자열 생성
        if (typeof setCategory === "function") {
            const mainObj = mainCategories.find(
                (c) => c.biz_main_category_id === mainCategory
            );
            const subObj = subCategories.find(
                (c) => c.biz_sub_category_id === subCategory
            );
            const detailObj = detailCategories.find(
                (c) => c.biz_detail_category_id === draftDetail
            );

            const mainText = mainObj?.biz_main_category_name || "";
            const subText = subObj?.biz_sub_category_name || "";
            const detailText = detailObj?.biz_detail_category_name || "";

            const full = [mainText, subText, detailText].filter(Boolean).join(" > ");
            setCategory(full);   // 예: "음식점 > 한식음식점 > 백반/한정식"
        }

        setActiveSheet(null);
    };

    return (
        <>
            {/* 상단 3개 PlaceholderB */}
            <div className="flex flex-col gap-[12px]">
                <PlaceholderB
                    label="업종 분류"
                    required
                    placeholder="대분류"
                    mode="click"
                    value={mainLabel}
                    rightIconMode="static"
                    rightIcon={<img src={down} alt="대분류 선택" />}
                    onClick={openMainSheet}
                />

                <PlaceholderB
                    placeholder="중분류"
                    mode="click"
                    value={subLabel}
                    rightIconMode="static"
                    rightIcon={<img src={down} alt="중분류 선택" />}
                    onClick={hasMain ? openSubSheet : undefined}
                />

                <PlaceholderB
                    placeholder="소분류"
                    mode="click"
                    value={detailLabel}
                    rightIconMode="static"
                    rightIcon={<img src={down} alt="소분류 선택" />}
                    onClick={hasMain && hasSub ? openDetailSheet : undefined}
                />
            </div>

            {/* ========= 대분류 바텀시트 ========= */}
            <BottomWithButton
                isOpen={activeSheet === "main"}
                onClose={closeSheet}
                title="업종 분류(대분류)"
                subtitle={""}
                onSubmit={handleConfirmMain}                     // 저장 클릭 시 실행
                buttonLabel="저장"
                buttonDisabled={!draftMain || draftMain === "0"} // 선택 안 했으면 비활성
                renderButton={({ onClick, disabled }) => (
                    <SplitRowQuarter
                        leftText="닫기"
                        onLeft={closeSheet}
                        rightType="fill"
                        rightText="저장"
                        onRight={onClick}
                        rightDisabled={disabled}
                    />
                )}
            >
                <div className="overflow-y-auto">
                    {mainOptions.map((opt) => {
                        const selected = draftMain === opt.value;
                        return (
                            <button
                                key={opt.value}
                                type="button"
                                onClick={() => setDraftMain(opt.value)}
                                className={`
                                    w-full text-left p-[12px] rounded-[8px]
                                    text-b1 text-text-subtle
                                    ${selected && "bg-surface-light-primary-light"}
                                    `}
                            >
                                {opt.label}
                            </button>
                        );
                    })}
                </div>
            </BottomWithButton>

            {/* ========= 중분류 바텀시트 ========= */}
            <BottomWithButton
                isOpen={activeSheet === "sub"}
                onClose={closeSheet}
                title="업종 분류(대분류)"
                subtitle={""}
                topOffset={"60px"}
                onSubmit={handleConfirmSub}
                buttonLabel="저장"
                buttonDisabled={!draftSub || draftSub === "0"}
                renderButton={({ onClick, disabled }) => (
                    <SplitRowQuarter
                        leftText="닫기"
                        onLeft={closeSheet}
                        rightType="fill"
                        rightText="저장"
                        onRight={onClick}
                        rightDisabled={disabled}
                    />
                )}
            >
                <div className="flex flex-col gap-[4px] pb-[8px]">
                    {subOptions.map((opt) => {
                        const selected = draftSub === opt.value;
                        return (
                            <button
                                key={opt.value}
                                type="button"
                                onClick={() => setDraftSub(opt.value)}
                                className={`
                                    w-full text-left p-[12px] rounded-[8px]
                                    text-b1 text-text-subtle
                                    ${selected && "bg-surface-light-primary-light"}
                                    `}
                            >
                                {opt.label}
                            </button>
                        );
                    })}
                </div>
            </BottomWithButton>

            {/* ========= 소분류 바텀시트 ========= */}
            <BottomWithButton
                isOpen={activeSheet === "detail"}
                onClose={closeSheet}
                title="업종 분류(대분류)"
                subtitle=""
                topOffset={"60px"}
                onSubmit={handleConfirmDetail}
                buttonLabel="저장"
                buttonDisabled={!draftDetail || draftDetail === "0"}
                renderButton={({ onClick, disabled }) => (
                    <SplitRowQuarter
                        leftText="닫기"
                        onLeft={closeSheet}
                        rightType="fill"
                        rightText="저장"
                        onRight={onClick}
                        rightDisabled={disabled}
                    />
                )}
            >
                <div className="flex flex-col gap-[4px] pb-[8px]">
                    {detailOptions.map((opt) => {
                        const selected = draftDetail === opt.value;
                        return (
                            <button
                                key={opt.value}
                                type="button"
                                onClick={() => setDraftDetail(opt.value)}
                                className={`
                                    w-full text-left p-[12px] rounded-[8px]
                                    text-b1 text-text-subtle
                                    ${selected && "bg-surface-light-primary-light"}
                                    `}
                            >
                                {opt.label}
                            </button>
                        );
                    })}
                </div>
            </BottomWithButton>
        </>
    );
};

export default CategoryBottomSheet;
